// 汎用アラート/確認モーダルコンポーネント
import React from 'react';
import './AlertModal.scss';

/**
 * AlertModal - 統一されたモーダルコンポーネント
 * 
 * @param {boolean} open - モーダルの表示状態
 * @param {string} title - タイトル（'成功', 'エラー', '確認', etc）
 * @param {string} message - メッセージ内容
 * @param {function} onClose - モーダルを閉じる際のコールバック
 * @param {string} type - 'alert' | 'confirm' （デフォルト: 'alert'）
 * @param {function} onConfirm - confirmモードでOKを押した時のコールバック
 * @param {string} confirmText - 確認ボタンのテキスト（デフォルト: 'OK'）
 * @param {string} cancelText - キャンセルボタンのテキスト（デフォルト: 'キャンセル'）
 * @param {string} iconType - アイコンタイプを直接指定 ('success', 'error', 'info', 'warning', 'confirm')
 */
const AlertModal = ({ 
  open, 
  title, 
  message, 
  onClose,
  type = 'alert',
  onConfirm,
  confirmText = 'OK',
  cancelText = 'キャンセル',
  iconType: iconTypeProp
}) => {
  if (!open) return null;

  const getIconType = () => {
    // propsで直接指定されている場合はそれを使用
    if (iconTypeProp) return iconTypeProp;
    
    // タイトルから推測
    if (title === 'エラー' || title?.includes('失敗')) return 'error';
    if (title === '成功' || title?.includes('完了') || title?.includes('しました')) return 'success';
    if (title === '確認' || type === 'confirm') return 'confirm';
    if (title === '警告' || title?.includes('注意')) return 'warning';
    return 'info';
  };

  const renderIcon = () => {
    const iconType = getIconType();
    
    if (iconType === 'success') {
      return (
        <div className="mc-alert-icon-container success">
          <svg className="mc-alert-icon-svg" viewBox="0 0 52 52">
            <circle className="mc-alert-icon-circle" cx="26" cy="26" r="25" fill="none"/>
            <path className="mc-alert-icon-check" fill="none" d="M14.1 27.2l7.1 7.2 16.7-16.8"/>
          </svg>
        </div>
      );
    } else if (iconType === 'error') {
      return (
        <div className="mc-alert-icon-container error">
          <svg className="mc-alert-icon-svg" viewBox="0 0 52 52">
            <circle className="mc-alert-icon-circle" cx="26" cy="26" r="25" fill="none"/>
            <path className="mc-alert-icon-line" fill="none" d="M16 16 36 36 M36 16 16 36"/>
          </svg>
        </div>
      );
    } else if (iconType === 'warning') {
      return (
        <div className="mc-alert-icon-container warning">
          <svg className="mc-alert-icon-svg" viewBox="0 0 52 52">
            <circle className="mc-alert-icon-circle" cx="26" cy="26" r="25" fill="none"/>
            <line className="mc-alert-icon-line" x1="26" y1="15" x2="26" y2="30" strokeWidth="3"/>
            <circle cx="26" cy="37" r="2.5" fill="currentColor" className="mc-alert-icon-dot"/>
          </svg>
        </div>
      );
    } else if (iconType === 'confirm') {
      return (
        <div className="mc-alert-icon-container confirm">
          <svg className="mc-alert-icon-svg" viewBox="0 0 52 52">
            <circle className="mc-alert-icon-circle" cx="26" cy="26" r="25" fill="none"/>
            <text x="26" y="33" textAnchor="middle" className="mc-alert-icon-question">?</text>
          </svg>
        </div>
      );
    } else {
      return (
        <div className="mc-alert-icon-container info">
          <svg className="mc-alert-icon-svg" viewBox="0 0 52 52">
            <circle className="mc-alert-icon-circle" cx="26" cy="26" r="25" fill="none"/>
            <line className="mc-alert-icon-line" x1="26" y1="20" x2="26" y2="32" strokeWidth="3"/>
            <circle cx="26" cy="15" r="2" fill="currentColor"/>
          </svg>
        </div>
      );
    }
  };

  const handleConfirm = () => {
    if (onConfirm) {
      onConfirm();
    }
    onClose();
  };

  const handleCancel = () => {
    onClose();
  };

  const isConfirmMode = type === 'confirm';

  return (
    <div className="mc-alert-overlay" onClick={handleCancel}>
      <div className="mc-alert-modal" onClick={(e) => e.stopPropagation()}>
        <div className="mc-alert-header">
          {renderIcon()}
          <h3 className="mc-alert-title">{title || 'お知らせ'}</h3>
        </div>
        <div className="mc-alert-body">
          <p className="mc-alert-message">{message}</p>
          
          {isConfirmMode ? (
            <div className="mc-alert-buttons">
              <button className="mc-alert-button mc-alert-button--cancel" onClick={handleCancel}>
                {cancelText}
              </button>
              <button className="mc-alert-button mc-alert-button--confirm" onClick={handleConfirm}>
                {confirmText}
              </button>
            </div>
          ) : (
            <button className="mc-alert-button" onClick={onClose}>
              {confirmText}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default AlertModal;
